import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';

import { format } from 'date-fns';
import React, { CSSProperties } from 'react';
import { getDateLocale } from 'shared/i18n';
import { UDImage } from 'ud-ui/components';
import { GameDetails, GameDetailsTeamMember, GameEventType } from 'modules/games/domain/interfaces/game-details';

type GoalEvent = {
  period: number;
  minutes: number;
  seconds: number;
};

type PlayerWithEvents = GameDetailsTeamMember & {
  goals: string[];
};

type PlayerCard = {
  period: number;
  minutes: number;
  seconds: number;
  type: GameEventType.YELLOW_CARD | GameEventType.RED_CARD;
  player: GameDetailsTeamMember;
};

const ProtocolContainer = styled.div`
  width: 100%;
  height: 100%;
  font-family: Arial, serif;
  font-size: 10px;

  table {
    width: 98%;
  }

  table,
  th,
  td {
    border: 1px solid black;
    border-collapse: collapse;
  }

  .protocol-table {
    th {
      height: 24px;
    }

    td {
      text-align: left;
      padding: 4px;
    }

    td.text-center {
      text-align: center;
      padding: 0;
    }
  }
`;

const TeamTitle = styled.div`
  text-align: center;
  font-weight: bold;
  margin: 10px 0 6px;
`;

const PlayersTable = (props: { players: PlayerWithEvents[] }) => {
  const { players } = props;
  const itemsCount = Math.max(16, players.length);
  const items = Array(itemsCount).fill(null);

  return (
    <table className="protocol-table">
      <thead>
        <tr>
          <th>#</th>
          <th>
            <Trans>ФИО</Trans>
          </th>
          <th>
            <Trans>Голы (мин.)</Trans>
          </th>
          <th>
            <Trans>Передачи (мин.)</Trans>
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((_, index) => (
          <tr key={index}>
            <td className="text-center" width="36px">
              {players[index]?.number}
            </td>
            <td>
              {players[index]?.lastName} {players[index]?.firstName}
            </td>
            <td width="80px">{players[index]?.goals.join(', ')}</td>
            <td width="80px">&nbsp;</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const CardsTable = (props: { cards?: PlayerCard[] }) => {
  const cards = props.cards ?? [];
  const itemsCount = Math.max(6, cards.length);
  const items = Array(itemsCount).fill(null);
  const { t } = useLingui();

  const cardTypeText = {
    [GameEventType.YELLOW_CARD]: t({ message: 'Пр.', comment: 'Предупреждение (жёлтая карточка) в протоколе матча' }),
    [GameEventType.RED_CARD]: t({ message: 'Уд.', comment: 'Удаление (красная карточка) в протоколе матча' }),
    undefined: '',
  };

  return (
    <table className="protocol-table">
      <thead>
        <tr>
          <th>
            <Trans>Мин.</Trans>
          </th>
          <th>
            <Trans comment="Предупреждение / удаление">Пр/Уд</Trans>
          </th>
          <th>
            <Trans>ФИО</Trans>
          </th>
          <th>
            <Trans>Причина</Trans>
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((_, index) => (
          <tr key={index}>
            <td className="text-center" width="42px">
              {cards[index]?.minutes}
            </td>
            <td className="text-center" width="48px">
              {cardTypeText[cards[index]?.type]}
            </td>
            <td>
              {cards[index]?.player.lastName} {cards[index]?.player.firstName}
            </td>
            <td width="92px">&nbsp;</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

interface UnderlinedTextProps {
  width?: string;
  textAlign?: 'center' | 'left' | 'right';
  display?: 'block' | 'inline-block' | 'inline' | 'none';
}

const UnderlinedText = styled.div`
  border-bottom: 1px solid black;
  width: ${(props: UnderlinedTextProps) => props.width ?? 'auto'};
  text-align: ${(props: UnderlinedTextProps) => props.textAlign ?? 'left'};
  display: ${(props: UnderlinedTextProps) => props.display ?? 'block'};
`;

const TwoColumnsProtocolRow = (props: { title: string }) => (
  <div className="row" style={{ marginTop: '10px' }}>
    <div className="w-1/2">
      <div className="row">
        <div className="w-5/12">{props.title}</div>
        <div className="w-1/2" style={{ width: '56%' }}>
          <UnderlinedText>&nbsp;</UnderlinedText>
        </div>
      </div>
    </div>

    <div className="w-1/2">
      <div className="row">
        <div className="w-5/12">{props.title}</div>
        <div className="w-1/2" style={{ width: '56%' }}>
          <UnderlinedText>&nbsp;</UnderlinedText>
        </div>
      </div>
    </div>
  </div>
);

interface MatchProtocolToPrintProps {
  matchDetails: GameDetails | null;
  logoUrl?: string;
}

const styles: Record<string, CSSProperties> = {
  container: { width: '100%', height: '100%', fontFamily: 'Arial', fontSize: '12px' },
  infoLeftBlock: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0 16px 0 0',
    marginBottom: '16px',
  },
  infoRightBlock: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0 16px 0 0',
    marginBottom: '16px',
  },
  placeInfo: {
    display: 'flex',
  },
};

export const MatchProtocolToPrint = React.forwardRef((props: MatchProtocolToPrintProps, ref: React.ForwardedRef<HTMLDivElement>) => {
  const { matchDetails, logoUrl } = props;
  const { t, i18n } = useLingui();
  if (!matchDetails)
    return (
      <ProtocolContainer ref={ref}>
        <Trans>Загрузка...</Trans>
      </ProtocolContainer>
    );

  const playersById: Record<string, GameDetailsTeamMember> = {};
  matchDetails?.teams.forEach((team) => {
    team.members.forEach((member) => {
      playersById[member.id] = member;
    });
  });

  const goalsByMember: Record<string, GoalEvent[]> = {};
  const teamsCards: Record<string, PlayerCard[]> = {};

  matchDetails?.events.forEach((event) => {
    const { type, period, minutes, seconds } = event;

    if (type === GameEventType.GOAL || /*type === GameEventType.AFTER_MATCH_PENALTY_GOAL ||*/ type === GameEventType.PASS_AND_GOAL) {
      let playerId: number;

      if (type === GameEventType.PASS_AND_GOAL) {
        [, playerId] = event.playerIds;
      } else {
        [playerId] = event.playerIds;
      }

      goalsByMember[playerId] = [...(goalsByMember[playerId] || []), { period, minutes, seconds }];
    }

    if (type === GameEventType.YELLOW_CARD || type === GameEventType.RED_CARD) {
      const { teamId } = event;
      const [playerId] = event.playerIds;
      const player = playersById[playerId] || null;

      if (player) {
        teamsCards[teamId] = [
          ...(teamsCards[teamId] || []),
          {
            type,
            period,
            minutes,
            seconds,
            player,
          },
        ];
      }
    }
  });

  const teamsPlayers: Array<PlayerWithEvents[]> = [];
  matchDetails?.teams.forEach((team) => {
    const players: PlayerWithEvents[] = [];
    team.members.forEach((member) => {
      const goalsByPeriod = (goalsByMember[member.id] || []).reduce<Record<string, string[]>>((acc, item) => {
        return {
          ...acc,
          [item.period]: [...(acc[item.period] || []), `${item.minutes}'`],
        };
      }, {});

      const goals = Object.keys(goalsByPeriod).map((period) => {
        return `${period} (${goalsByPeriod[period].join(', ')})`;
      });

      players.push({
        ...member,
        goals,
      });
    });

    teamsPlayers.push(players);
  });

  const firstTeamScore = matchDetails?.result?.firstTeamScore ?? 0;
  const secondTeamScore = matchDetails?.result?.secondTeamScore ?? 0;
  const totalScore = matchDetails?.hasScore ? `${firstTeamScore} : ${secondTeamScore}` : '';

  let winner: string = '';
  if (firstTeamScore > secondTeamScore) {
    winner = matchDetails?.teams[0].name ?? '';
  } else if (secondTeamScore > firstTeamScore) {
    winner = matchDetails?.teams[1].name ?? '';
  }

  return (
    <ProtocolContainer ref={ref}>
      {!matchDetails ? (
        <div>
          <Trans>Загрузка...</Trans>
        </div>
      ) : (
        <div className="w-full px-6 py-6">
          <h2 style={{ marginBottom: '10px' }}>
            {/* {logoUrl && <img style={{ verticalAlign: 'middle', marginRight: '10px' }} src={logoUrl} height={32} alt={''} />} */}
            {logoUrl && <UDImage url={logoUrl} className={'h-8'} />}
            {matchDetails.tournament.name}
          </h2>
          <h2 style={{ marginBottom: '10px', textAlign: 'center' }}>{matchDetails.championship.name}</h2>
          <h2 style={{ marginBottom: '32px' }}>
            <Trans>Протокол матча</Trans>
          </h2>

          <div className="row">
            <div className="w-1/2">
              <div style={styles.infoLeftBlock}>
                <div>
                  <Trans>Место проведения</Trans>
                </div>
                <UnderlinedText width="200px" textAlign="center">
                  &nbsp;
                </UnderlinedText>
              </div>
              <TeamTitle>{matchDetails.teams[0].name}</TeamTitle>
              <PlayersTable players={teamsPlayers[0]} />
            </div>

            <div className="w-1/2">
              <div style={styles.infoRightBlock}>
                <div className="text">
                  <Trans>Дата и время проведения</Trans>
                </div>
                <UnderlinedText width="200px" textAlign="center">
                  {format(new Date(matchDetails.date), 'd MMMM yyyy HH:mm', { locale: getDateLocale(i18n.locale) })}
                </UnderlinedText>
              </div>
              <TeamTitle>{matchDetails.teams[1].name}</TeamTitle>
              <PlayersTable players={teamsPlayers[1]} />
            </div>
          </div>

          <h3 style={{ textAlign: 'center', margin: '16px 0' }}>
            <Trans>НАКАЗАНИЯ:</Trans>
          </h3>
          <div className="row">
            <div className="w-1/2">
              <CardsTable cards={teamsCards[matchDetails.teams[0].id]} />
            </div>
            <div className="w-1/2">
              <CardsTable cards={teamsCards[matchDetails.teams[1].id]} />
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <div className="row">
              <div className="w-1/2" style={{ paddingTop: '16px' }}>
                <span style={{ width: '13%', display: 'inline-block' }}>
                  <Trans>Результат</Trans>&nbsp;
                </span>
                <UnderlinedText display="inline-block" width="100px" textAlign="center" style={{ width: '24%', marginRight: '2%' }}>
                  {totalScore}&nbsp;
                </UnderlinedText>
                &nbsp;
                <span style={{ width: '13%', display: 'inline-block' }}>
                  <Trans>В пользу</Trans>&nbsp;
                </span>
                <UnderlinedText display="inline-block" width="160px" textAlign="center" style={{ width: '45%' }}>
                  {winner}&nbsp;
                </UnderlinedText>
              </div>

              <div className="w-1/2" style={{ paddingTop: '16px' }}>
                <span style={{ width: '20%', display: 'inline-block' }}>
                  <Trans>Счёт 1-й тайм</Trans>&nbsp;
                </span>
                <UnderlinedText display="inline-block" width="90px" textAlign="center" style={{ width: '18%', marginRight: '2%' }}>
                  &nbsp;
                </UnderlinedText>
                &nbsp;
                <span style={{ width: '12%', display: 'inline-block' }}>
                  <Trans>В пользу</Trans>&nbsp;
                </span>
                <UnderlinedText display="inline-block" width="150px" textAlign="center" style={{ width: '45%' }}>
                  &nbsp;
                </UnderlinedText>
              </div>
            </div>

            <div className="row" style={{ marginTop: '10px' }}>
              <div className="w-1/6" style={{ width: '15.5%' }}>
                <Trans>Лучший игрок матча</Trans>
              </div>
              <div className="w-5/6">
                <UnderlinedText>&nbsp;</UnderlinedText>
              </div>
            </div>

            <TwoColumnsProtocolRow title={t`Оценки за судейство (из 10)`} />
            <TwoColumnsProtocolRow title={t`Количество болельщиков`} />
            <TwoColumnsProtocolRow title={t`Дисциплина (оценка судьи)`} />
            <TwoColumnsProtocolRow title={t`Подпись капитана`} />
            <TwoColumnsProtocolRow title={t`Подпись капитана`} />

            <div className="row" style={{ marginTop: '10px' }}>
              <div className="w-1/2">
                <div className="row">
                  <div className="w-5/12">
                    <Trans>Главный судья</Trans>
                  </div>
                  <div className="w-1/2" style={{ width: '56%' }}>
                    <UnderlinedText>&nbsp;</UnderlinedText>
                  </div>
                </div>
              </div>

              <div className="w-1/2">
                <div className="row">
                  <div className="w-5/12">
                    <Trans>Подпись судьи</Trans>
                  </div>
                  <div className="w-1/2" style={{ width: '56%' }}>
                    <UnderlinedText>&nbsp;</UnderlinedText>
                  </div>
                </div>
              </div>
            </div>

            <div className="row" style={{ marginTop: '10px', width: '98.9%' }}>
              <div className="w-1/6">
                <Trans>Судейский корпус</Trans>
              </div>
              <div className="w-5/6">
                <UnderlinedText>&nbsp;</UnderlinedText>
              </div>
            </div>

            <div className="row" style={{ marginTop: '10px', width: '98.9%' }}>
              <div className="w-1/12">
                <Trans>Примечание</Trans>
              </div>
              <div className="w-11/12">
                <UnderlinedText>&nbsp;</UnderlinedText>
              </div>
            </div>

            <div style={{ marginTop: '10px', width: '98.9%' }}>
              <UnderlinedText>&nbsp;</UnderlinedText>
            </div>
            <div style={{ marginTop: '10px', width: '98.9%' }}>
              <UnderlinedText>&nbsp;</UnderlinedText>
            </div>
          </div>
        </div>
      )}
    </ProtocolContainer>
  );
});
